import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  Spin,
  Typography,
  Row,
  Col,
  Tabs,
  Table,
  Tag,
  Button,
  Space,
  Select,
  Input,
} from "antd";
import {
  ReloadOutlined,
  UserOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
} from "@ant-design/icons";
import { getValidationStatistics } from "../../../api/Services";
import styles from "./ClientValidationStatistics.module.scss";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const FIELDS = [
  { key: "email", label: "email" },
  { key: "tel", label: "tel" },
  { key: "doc", label: "doc" },
  { key: "fecha_nac", label: "fecha_nac" },
  { key: "sexo", label: "sexo" },
  { key: "ciudad", label: "ciudad" },
];

const SOURCES = [
  { key: "POS", label: "POS" },
  { key: "ECOMMERCE", label: "eCommerce" },
  { key: "OTHER", label: "Otro" },
];

const CHANNEL_HEALTH_DATA = [
  {
    key: "pos",
    canal: "POS",
    status: "WARN",
    eventos1h: 210,
    esperados1h: 300,
    errores1h: 90,
    latencia: "12s",
    ultimoOK: "10:41",
  },
  {
    key: "ecommerce",
    canal: "eCommerce",
    status: "OK",
    eventos1h: 580,
    esperados1h: 580,
    errores1h: 0,
    latencia: "1s",
    ultimoOK: "10:42",
  },
  {
    key: "other",
    canal: "Otro",
    status: "FAIL",
    eventos1h: 0,
    esperados1h: 120,
    errores1h: 120,
    latencia: "-",
    ultimoOK: "09:55",
  },
];

interface FieldValidationStatistics {
  correct?: number;
  incorrect?: number;
  duplicate?: number;
  correctPercentage?: number;
  incorrectPercentage?: number;
  duplicatePercentage?: number;
  invalidFormat?: number;
  emptyOrNull?: number;
  invalidValue?: number;
  incorrectLength?: number;
  improbableDate?: number;
  advancedAge?: number;
  minor?: number;
  notSpecified?: number;
  missing?: number;
}

interface SourceValidationStatistics {
  source?: string;
  totalClients?: number;
  fieldCompleteness?: {
    email?: number;
    tel?: number;
    doc?: number;
    fecha_nac?: number;
    sexo?: number;
    ciudad?: number;
  };
}

interface ValidationStatisticsData {
  computedAt?: string;
  enabled?: boolean;
  totalClients?: number;
  email?: FieldValidationStatistics;
  phone?: FieldValidationStatistics;
  mobile?: FieldValidationStatistics;
  birthdate?: FieldValidationStatistics;
  sex?: FieldValidationStatistics;
  name?: FieldValidationStatistics;
  lastName?: FieldValidationStatistics;
  documentType?: FieldValidationStatistics;
  documentNumber?: FieldValidationStatistics;
  contact?: FieldValidationStatistics;
  country?: FieldValidationStatistics;
  locality?: FieldValidationStatistics;
  statisticsBySource?: SourceValidationStatistics[];
}

interface ClientValidationStatisticsProps {
  embedded?: boolean;
}

const ClientValidationStatistics = ({
  embedded = false,
}: ClientValidationStatisticsProps) => {
  const { t } = useTranslation();
  const containerClass = embedded ? styles.embeddedContainer : styles.container;
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState<ValidationStatisticsData | null>(
    null
  );
  const [activeTab, setActiveTab] = useState("overview");
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const fetchStatistics = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getValidationStatistics();
      setStatistics(data);
      setLastUpdate(data?.computedAt ? new Date(data.computedAt) : new Date());
    } catch (error) {
      console.error("Error fetching validation statistics:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  const getCompletenessColor = useCallback((percentage: number): string => {
    if (percentage < 60) return "#f5222d";
    if (percentage < 80) return "#faad14";
    return "#52c41a";
  }, []);

  const getCompletenessBgColor = useCallback((percentage: number): string => {
    if (percentage < 60) return "#ffebee";
    if (percentage < 80) return "#fff8e1";
    return "#e8f5e9";
  }, []);

  const activablePercentage = useMemo(() => {
    if (!statistics?.totalClients) return 0;
    const total = statistics.totalClients;
    const activable = statistics.contact?.correct || 0;
    return total > 0 ? (activable / total) * 100 : 0;
  }, [statistics]);

  const conflictPercentage = useMemo(
    () => 100 - activablePercentage,
    [activablePercentage]
  );

  const matrixData = useMemo(() => {
    const sourceStatsMap = new Map<string, SourceValidationStatistics>();
    if (statistics?.statisticsBySource) {
      statistics.statisticsBySource.forEach((stat) => {
        if (stat.source) {
          sourceStatsMap.set(stat.source, stat);
        }
      });
    }

    return SOURCES.map((source) => {
      const sourceStat = sourceStatsMap.get(source.key);
      const sourceData: any = {
        key: source.key.toLowerCase(),
        canal: source.label,
      };

      FIELDS.forEach((field) => {
        const completeness =
          sourceStat?.fieldCompleteness?.[
            field.key as keyof typeof sourceStat.fieldCompleteness
          ] ?? 0;
        sourceData[field.key] = completeness;
      });

      return sourceData;
    });
  }, [statistics]);

  const topProblems = useMemo(() => {
    const problems: any[] = [];

    if (
      statistics?.email &&
      statistics.email.incorrectPercentage &&
      statistics.email.incorrectPercentage > 0
    ) {
      problems.push({
        key: "email",
        campo: "email",
        canal: "POS",
        delta: -12,
        porcentajeValido: statistics.email.correctPercentage || 0,
        afectados: Math.round((statistics.email.incorrect || 0) / 1000) * 1000,
      });
    }

    if (
      statistics?.documentNumber &&
      statistics.documentNumber.incorrectPercentage &&
      statistics.documentNumber.incorrectPercentage > 0
    ) {
      problems.push({
        key: "doc",
        campo: "doc",
        canal: "POS",
        delta: -8,
        porcentajeValido: statistics.documentNumber.correctPercentage || 0,
        afectados:
          Math.round((statistics.documentNumber.incorrect || 0) / 1000) * 1000,
      });
    }

    if (
      statistics?.birthdate &&
      statistics.birthdate.incorrectPercentage &&
      statistics.birthdate.incorrectPercentage > 0
    ) {
      problems.push({
        key: "fecha_nac",
        campo: "fecha_nac",
        canal: "POS",
        delta: -5,
        porcentajeValido: statistics.birthdate.correctPercentage || 0,
        afectados:
          Math.round((statistics.birthdate.incorrect || 0) / 1000) * 1000,
      });
    }

    return problems;
  }, [statistics]);

  const topProblemsColumns = useMemo(
    () => [
      {
        title: t("ClientValidationStats-TableField"),
        dataIndex: "campo",
        key: "campo",
      },
      {
        title: t("ClientValidationStats-TableChannel"),
        dataIndex: "canal",
        key: "canal",
      },
      {
        title: t("ClientValidationStats-TableDelta7d"),
        dataIndex: "delta",
        key: "delta",
        render: (delta: number) => (
          <span
            style={{
              color: delta < 0 ? "#f5222d" : "#52c41a",
              fontWeight: 600,
            }}
          >
            {delta > 0 ? "+" : ""}
            {delta} {t("ClientValidationStats-PercentagePoints")}
          </span>
        ),
      },
      {
        title: t("ClientValidationStats-TableValidPercent"),
        dataIndex: "porcentajeValido",
        key: "porcentajeValido",
        render: (value: number) => `${value.toFixed(1)}%`,
      },
      {
        title: t("ClientValidationStats-TableAffected"),
        dataIndex: "afectados",
        key: "afectados",
        render: (value: number) => value.toLocaleString(),
      },
      {
        title: t("ClientValidationStats-TableAction"),
        key: "action",
        render: () => (
          <Button type="link" size="small">
            {t("ClientValidationStats-View")}
          </Button>
        ),
      },
    ],
    [t]
  );

  const matrixColumns = useMemo(
    () => [
      {
        title: t("ClientValidationStats-TableChannel"),
        dataIndex: "canal",
        key: "canal",
        width: 120,
        render: (text: string) => (
          <span style={{ fontWeight: 600, fontSize: "14px" }}>{text}</span>
        ),
      },
      ...FIELDS.map((field) => ({
        title: field.label,
        dataIndex: field.key,
        key: field.key,
        align: "center" as const,
        render: (value: number) => {
          const color = getCompletenessColor(value);
          const bgColor = getCompletenessBgColor(value);
          return (
            <div
              className={styles.percentageCell}
              style={{
                backgroundColor: bgColor,
                color: color,
                borderColor: color,
                borderWidth: "2px",
              }}
            >
              {value.toFixed(0)}%
            </div>
          );
        },
      })),
    ],
    [getCompletenessColor, getCompletenessBgColor, t]
  );

  const channelHealthColumns = useMemo(
    () => [
      {
        title: t("ClientValidationStats-TableChannel"),
        dataIndex: "canal",
        key: "canal",
        width: 140,
        render: (text: string, record: any) => (
          <Space>
            {record.status === "OK" && (
              <CheckCircleFilled style={{ color: "#52c41a" }} />
            )}
            {record.status === "WARN" && (
              <ThunderboltOutlined style={{ color: "#faad14" }} />
            )}
            {record.status === "FAIL" && (
              <CloseCircleFilled style={{ color: "#f5222d" }} />
            )}
            <span style={{ fontWeight: 500 }}>{text}</span>
          </Space>
        ),
      },
      {
        title: t("ClientValidationStats-TableStatus"),
        dataIndex: "status",
        key: "status",
        width: 120,
        render: (status: string) => {
          const colorMap: Record<string, string> = {
            OK: "green",
            WARN: "orange",
            FAIL: "red",
          };
          return <Tag color={colorMap[status]}>{status}</Tag>;
        },
      },
      {
        title: t("ClientValidationStats-TableEvents1h"),
        dataIndex: "eventos1h",
        key: "eventos1h",
        width: 130,
        align: "right" as const,
      },
      {
        title: t("ClientValidationStats-TableExpected1h"),
        dataIndex: "esperados1h",
        key: "esperados1h",
        width: 140,
        align: "right" as const,
      },
      {
        title: t("ClientValidationStats-TableLatency"),
        dataIndex: "latencia",
        key: "latencia",
        width: 130,
        align: "right" as const,
      },
      {
        title: t("ClientValidationStats-TableLastOK"),
        dataIndex: "ultimoOK",
        key: "ultimoOK",
        width: 130,
        align: "center" as const,
      },
      {
        title: t("ClientValidationStats-TableAction"),
        key: "action",
        width: 100,
        align: "center" as const,
        render: () => (
          <Button type="link" size="small">
            {t("ClientValidationStats-View")}
          </Button>
        ),
      },
    ],
    [t]
  );

  if (loading) {
    if (embedded) {
      return null;
    }
    return (
      <div className={containerClass}>
        <div className={styles.loadingContainer}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (statistics?.enabled === false) {
    if (embedded) {
      return null;
    }
    return (
      <div className={containerClass}>
        <Card>
          <Title level={4}>{t("ClientValidationStats-Disabled-Title")}</Title>
          <Text type="secondary">
            {t("ClientValidationStats-Disabled-Description")}
          </Text>
        </Card>
      </div>
    );
  }

  if (!statistics) {
    if (embedded) {
      return null;
    }
    return (
      <div className={containerClass}>
        <Card>
          <Title level={4}>{t("ClientValidationStats-NoDataAvailable")}</Title>
        </Card>
      </div>
    );
  }

  return (
    <div className={containerClass}>
      <div className={styles.header}>
        <div>
          <Title level={2} className={styles.mainTitle}>
            {t("ClientValidationStats-Title")}
          </Title>
          <Text className={styles.subtitle}>
            {t("ClientValidationStats-Subtitle")}
          </Text>
        </div>
        <Space>
          <Select defaultValue="7" style={{ width: 150 }}>
            <Select.Option value="7">{t("ClientValidationStats-Last7Days")}</Select.Option>
            <Select.Option value="30">{t("ClientValidationStats-Last30Days")}</Select.Option>
            <Select.Option value="90">{t("ClientValidationStats-Last90Days")}</Select.Option>
          </Select>
          <Input
            placeholder={t("ClientValidationStats-FilterPlaceholder")}
            style={{ width: 250 }}
          />
          <Button icon={<ReloadOutlined />} onClick={fetchStatistics}>
            {t("ClientValidationStats-Update")}
          </Button>
        </Space>
      </div>

      <div className={styles.kpisSection}>
        <Row gutter={[16, 16]} className={styles.kpiRow}>
        <Col xs={24} sm={12} lg={6}>
          <Card className={styles.kpiCard}>
            <div className={styles.kpiContent}>
              <UserOutlined
                className={styles.kpiIcon}
                style={{ color: "#1890ff" }}
              />
              <div>
                <div className={styles.kpiValue}>
                  {statistics.totalClients?.toLocaleString() || 0}
                </div>
                <div className={styles.kpiTitle}>{t("ClientValidationStats-ActiveClients")}</div>
                <div className={styles.kpiDescription}>{t("ClientValidationStats-Last24Months")}</div>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className={styles.kpiCard}>
            <div className={styles.kpiContent}>
              <CheckCircleOutlined
                className={styles.kpiIcon}
                style={{ color: "#52c41a" }}
              />
              <div>
                <div className={styles.kpiValue} style={{ color: "#52c41a" }}>
                  {activablePercentage.toFixed(0)}%
                </div>
                <div className={styles.kpiTitle}>{t("ClientValidationStats-Activables")}</div>
                <div className={styles.kpiDescription}>{t("ClientValidationStats-ValidMailOrCell")}</div>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className={styles.kpiCard}>
            <div className={styles.kpiContent}>
              <ExclamationCircleOutlined
                className={styles.kpiIcon}
                style={{ color: "#f5222d" }}
              />
              <div>
                <div className={styles.kpiValue} style={{ color: "#f5222d" }}>
                  {conflictPercentage.toFixed(0)}%
                </div>
                <div className={styles.kpiTitle}>{t("ClientValidationStats-WithConflict")}</div>
                <div className={styles.kpiDescription}>
                  {t("ClientValidationStats-ConflictFormula", {
                    percentage: activablePercentage.toFixed(0),
                  })}
                </div>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className={styles.kpiCard}>
            <div className={styles.kpiContent}>
              <ClockCircleOutlined
                className={styles.kpiIcon}
                style={{ color: "#722ed1" }}
              />
              <div>
                <div
                  className={styles.kpiValue}
                  style={{ color: "#722ed1", fontSize: "20px" }}
                >
                  {lastUpdate.toLocaleTimeString("es-ES", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </div>
                <div className={styles.kpiTitle}>{t("ClientValidationStats-LastUpdate")}</div>
                <div className={styles.kpiDescription}>
                  {(() => {
                    const today = new Date();
                    const updateDate = lastUpdate;
                    const isToday =
                      updateDate.getDate() === today.getDate() &&
                      updateDate.getMonth() === today.getMonth() &&
                      updateDate.getFullYear() === today.getFullYear();

                    if (isToday) {
                      return `${t("ClientValidationStats-Today")} ${updateDate.toLocaleDateString("es-ES", {
                        day: "2-digit",
                        month: "2-digit",
                      })}`;
                    } else {
                      return updateDate.toLocaleDateString("es-ES", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      });
                    }
                  })()}
                </div>
              </div>
            </div>
          </Card>
        </Col>
        </Row>
      </div>

      <Card className={styles.contentCard}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          className={styles.tabs}
        >
          <TabPane tab={t("ClientValidationStats-TabOverview")} key="overview">
            <div className={styles.overviewContent}>
              <Card
                title={t("ClientValidationStats-CompletenessByField")}
                className={styles.matrixCard}
                extra={
                  <div className={styles.legend}>
                    <Tag color="#f5222d">{"< 60%"}</Tag>
                    <Tag color="#faad14">{"60-80%"}</Tag>
                    <Tag color="#52c41a">{"> 80%"}</Tag>
                  </div>
                }
              >
                <Table
                  dataSource={matrixData}
                  columns={matrixColumns}
                  pagination={false}
                  size="small"
                  className={styles.matrixTable}
                />
              </Card>

              <Card
                title={t("ClientValidationStats-TopProblems")}
                className={styles.problemsCard}
              >
                <Table
                  dataSource={topProblems}
                  columns={topProblemsColumns}
                  pagination={false}
                  size="small"
                />
              </Card>
            </div>
          </TabPane>
          <TabPane tab={t("ClientValidationStats-TabChannels")} key="channels">
            <div style={{ padding: "0" }}>
              <Card
                title={t("ClientValidationStats-ChannelHealth")}
                className={styles.channelHealthCard}
              >
                <Text
                  type="secondary"
                  style={{
                    display: "block",
                    marginBottom: 32,
                    fontSize: "14px",
                    lineHeight: "1.6",
                  }}
                >
                  {t("ClientValidationStats-ChannelHealthDescription")}
                </Text>
                <Table
                  dataSource={CHANNEL_HEALTH_DATA}
                  columns={channelHealthColumns}
                  pagination={false}
                  size="small"
                  className={styles.channelHealthTable}
                />
                <div className={styles.footer}>
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    {t("ClientValidationStats-Source")}
                  </Text>
                </div>
              </Card>
            </div>
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default ClientValidationStatistics;
